/** @module Camunda/TaskView */
define([
	"dojo/_base/declare",
	"dijit/TitlePane",
	"underscore",
	"csi/_base/Deferred",
	"csi",
	"csi/action/Controller",
	"csi/action/FormData",
	"csi/card/_base",
	"csi/core/extendedPathService",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/def",
	"csi/def/Action",
	"csi/def/Heading",
	"csi/gallery/GalleryPlaceholder",
	"csi/html",
	"csi/html/Widget",
	"csi/locale",
	"csi/middleware/attributes/attributeKeysService",
	"csi/model/image",
	"csi/reflection",
	"csi/service/viewService",
	"csi/state",
	"csi/table/Relationship",
	"csi/table/View",
	"csi/task/util",
	"csi/types",
	"csi/view",
	"csi/xml",
], function(declare, TitlePane, _, Deferred, csi, Controller, FormData, csiCard, {parseExtendedPath}, ExtendedPath, Key, csiDef, Action, Heading, GalleryPlaceholder,
	html, Widget, locale, {prefixAttributeKeys}, {IMAGE_SIZE_SMALL}, reflection, {getClassDefaultPropertyView},
	state, TableRelationship, TableView, TaskUtil, types, csiView, xml) {

	const TYPE_TASK = "CamundaTask";

	const KEY_CONTEXT = "context";
	const KEY_DESC = "desc";
	const KEY_TITLE = "title";
	const KEY_VIEW = "view";
	const KEY_VIEW_NAME = "viewName";

	/**
	 * Create title pane at given location and return DOMNode where to add content
	 * @param {string} titleResource
	 * @param {DOMNode} domNode
	 * @return {DOMNode}
	 */
	function _createTitlePane(titleResource, domNode) {
		const w = new TitlePane({
			"class": "csi-title-pane-white",
			title: locale.resource(titleResource)
		}).placeAt(domNode);

		return w.containerNode;
	}

	/**
	 * Combine headings from property view with task heading, including adding a path prefix to all since view now originates from task
	 * @param {Heading[]} headings
	 * @param {ExtendedPath} instancePath
	 * @return {Heading[]}
	 */
	function _getDefaultViewHeadings(headings, instancePath) {
		const prefixPath = instancePath.getStandardPath();
		const taskHeading = _.extend(new Heading(), {
			classRenderer: "Camunda/TaskCardRenderer",
			columnWidth: types.LARGE,
			m_prop: xml.PROP_NAME
		});

		headings.forEach((h) => {
			h.m_path = xml.path(prefixPath, h.m_path);
		});

		return [taskHeading, ...headings];
	}

	/**
	 * Return path from CamundaTask to node owning property view (normally primary type)
	 * @param {ExtendedPath} pathToView
	 * @return {ExtendedPath}
	 */
	function _getTaskInstancePath(pathToView) {
		const ep = new ExtendedPath(TYPE_TASK, null, true);
		ep.addRelationStep("C8Instance", pathToView.getStartType());
		ep.append(pathToView);
		return ep;
	}


	return declare(Widget, {
		flexHeightPropagetesToChildren: true,

		/** @property {Node} taskDefinition - node holding view configuration for task */
		taskDefinition: null,

		/** @property {Result} taskGroupResult - batched group query result */
		taskGroupResult: null,

		/** @property {Node} taskInstance - node instance associated with the task */
		taskInstance: null,

		/** @property {DOMNode} _taskTitleNode - DOM node where to render title for task view */
		_taskTitleNode: null,

		/** @property {DOMNode} _taskViewNameNode - DOM node where to render name of task view */
		_taskViewNameNode: null,

		/**
		 * Extension point called when task selected in batch table
		 * @param {Context} ctx
		 * @param {string} url
		 * @param {boolean} bOn
		 * @param {boolean} bReplaced - not used since multi-select
		 */
		onSelectBatchTask: function(ctx, url, bOn, bReplaced) { },

		/**
		 * @override
		 * Initialize class members
		 */
		onViewStartBuild: function() {
			const cache = this.cacheView;
			const task = this.getViewNode();
			this.taskDefinition = cache.getChildNode(task, "TaskDefinition");
			this.taskInstance = cache.getChildNode(task, "C8Instance");
		},

		/**
		 * @override
		 * Custom processing of view keys
		 */
		_processKey: function(ctx, key, def, domNode) {
			const instance = this.taskInstance;
			const definition = this.taskDefinition;

			switch (key) {
				case KEY_CONTEXT:
					if (instance && definition.ViewDefinitionPath && !TaskUtil.taskDefinitionHasPropertyView(definition)) {
						const contextNode = _createTitlePane("ContextView", domNode);
						this._deferredFunctions.push(this._createContextView.bind(this, contextNode));
					}
					break;

				case KEY_DESC:
					// Don't create pane if no description
					const taskNode = this.getViewNode();
					const taskDefinitionUrl = taskNode.TaskDefinition;
					const taskDefinitionNode = this.getCache().getNode(taskDefinitionUrl);
					const info = taskDefinitionNode.Info;
					const siteLocale = state.get("siteLocale");
					const text = info[siteLocale];
					if (text) {
						const descNode = _createTitlePane("CamundaTaskDetails", domNode);
						html.multiLineText(descNode, text);
					}
					break;

				case KEY_TITLE:
					this._taskTitleNode = domNode;
					break;

				case KEY_VIEW:
					if (definition && definition.ViewDefinitionPath) {
						this._deferredFunctions.push(this._createCustomView.bind(this, domNode));
					}
					break;

				case KEY_VIEW_NAME:
					this._taskViewNameNode = domNode;
					break;
			}
		},

		/**
		 * Insert context view into view
		 * @param {DOMNode} domNode
		 * @return {Deferred}
		 */
		_createContextView: function(domNode) {
			const instance = this.taskInstance;
			const type = instance.$Type;
			const rc = reflection.getClass(type);
			const {imageSpec} = csiCard.getAttributeCardSpecs(type);

			const defView = getClassDefaultPropertyView(rc, {
				bReadOnly: true,
				defaultImageSize: IMAGE_SIZE_SMALL,
				supportsAttributeGroups: false,
				m_images: imageSpec ? [imageSpec.heading] : null,
			});

			return this._insertAndCreateView(defView, instance, domNode, {
				disableCustomViewToolbar: true,
				plugins: [],
				propertyColumns: 2,
				propertyForceImageDimensions: true,
				urlDefaultView: csi.EMPTY_URL,
			});
		},

		/**
		 * Insert context view into view
		 * @param {DOMNode} domNode
		 * @return {Deferred}
		 */
		_createCustomView: function(domNode) {
			const cache = this.cacheView;
			const definition = this.taskDefinition;
			const urlCustomView = definition.CustomView;
			const instance = this.taskInstance;
			const taskGroupResult = this.taskGroupResult;
			const ep = parseExtendedPath(definition.ViewDefinitionPath);
			const id = csiDef.decomposeViewKey(definition.ViewStoreId).id;
			var path;
			var dfd;

			if (instance && (ep.depth > 0)) {
				path = ep.getStandardPath();
				dfd = cache.getNodeDependencies(instance.$URL, [path]);
			} else {
				dfd = new Deferred().resolve();
			}

			return dfd.then(() => {
				const node = cache.getPathNode(instance, path);
				const defView = csiDef.getNodeViewDef(node, id);

				if (defView) {
					// render title and view name
					var title = locale.resource("CamundaTaskView");
					var viewName = "";

					if (id !== csiDef.getHostViewId()) {
						title += ":";
						viewName = locale.resource("ConjunctionTypeWithView", [locale.type(instance.$Type), locale.typeView(ep.getEndType(), id)]);
					}

					html.text(this._taskTitleNode, title);
					html.text(this._taskViewNameNode, viewName);

					// render view
					if (taskGroupResult) {
						// create table view for group of tasks - headings cannot be shared across views, so need both
						// view definition and an additional set of headings to use for the table view
						const propertyView = csiDef.getNodeViewDef(node, id);
						return this._createGroupView(defView, propertyView.m_headings, ep, urlCustomView, domNode);
					}

					return this._insertAndCreateView(defView, node, domNode, {
						allowAutomaticHeight: true,
						bUseLocation: false, // View inside FilmStrip should not modify page URL
						tabLocationType: node.$Type,
						urlDefaultView: urlCustomView
					});
				} else {
					// render placeholder notifying user of potential problem
					var title = "MissingView";
					var desc;
					var params;

					if (node) {
						desc = "CamundaTaskViewMissing";
					} else if (instance) {
						desc = "CamundaTaskViewNotReached";
					} else {
						title = "MissingPermissions";
						desc = "CamundaTaskViewNoInstance";
						params = [locale.type(ep.getStartType())];
					}

					$(domNode.parentNode).addClass("csi-camunda-task-view-placeholder");

					new GalleryPlaceholder({
						titleText: locale.resource(title),
						description: locale.resource(desc, params)
					}).placeAt(domNode);
				}
			});
		},

		/**
		 * Create grouped context view
		 * @param {View} defView - properties view to be converted into table
		 * @param {Heading[]} propertyHeadings
		 * @param {ExtendedPath} pathToView - path from primary type to view owner
		 * @param {string} urlCustomView - properties custom view to display
		 * @param {DOMNode} domNode
		 * @return {Deferred}
		 */
		async _createGroupView(defView, propertyHeadings, pathToView, urlCustomView, domNode) {
			const store = this.taskGroupResult.getStore();
			const instancePath = _getTaskInstancePath(pathToView);
			const viewParams = {
				allowAutomaticHeight: true,
				disableCustomViewToolbar: true,
				plugins: ["filter"],
				prefViewContext: defView.getTypeNode(),

				onCreateMainQueryLevel: function(ql) {
					// fill tasks in store from those in group
					ql.requestParameters = ql.requestParameters || {};
					ql.requestParameters["Qry.Limit.Path"] = store.resultUrls;
				}
			};

			try {
				// read keys from custom view and add prefix path to them from the task
				const view = csiView.newView(defView, null, {
					urlDefaultView: urlCustomView,
				});

				await view.loadDefaultCustomView();

				const {attributeKeys, hiddenKeys} = view;
				const taskKey = new Key({
					extendedPath: ExtendedPath.create(TYPE_TASK),
					prop: xml.PROP_NAME,
				}).typedString;

				viewParams.attributeKeys = [taskKey, ...prefixAttributeKeys(attributeKeys, instancePath)];
				viewParams.hiddenKeys = prefixAttributeKeys(hiddenKeys, instancePath);
			} catch (ex) {
				// use default view
			}

			const tableHeadings = _getDefaultViewHeadings(propertyHeadings, instancePath);
			const groupView = _.extend(new TableView(), {
				m_id: "__BatchTasks__",
				m_typeView: "Table",
				m_headings: tableHeadings,
				m_relationships: [
					_.extend(new TableRelationship(), {
						bOrdered: true,
						m_target: TYPE_TASK
					})
				]
			});

			// allow selections in view
			const controller = new Controller({
				m_action: new Action(),
				m_element: domNode
			});

			// TODO: use FormData.addSelectListener?
			groupView.setFormData(_.extend(new FormData(controller), {
				m_bEditable: true,
				onSelect: this.onSelectBatchTask
			}));

			return this._insertAndCreateView(groupView, this.getViewNode(), domNode, viewParams);
		}
	});
});
